import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import img1 from '../../../assets/school.png';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import { KVSLoader } from '../../../components/loaders';
import { getSchoolFullDetails } from '../services/api';
import * as LOOKUP from '../constants/lookupMaps';

interface ContactDetail {
  id: number;
  role: number;
  roleName: string;
  name: string;
  contactNo: string;
  emailID: string;
}

interface SchoolFullDetails {
  schoolProfile?: any;
  principalDetails?: any;
  locationDetails?: any;
  administrativeDetails?: any;
  infrastructureDetails?: any;
  labProfiles?: any;
  facilityProfiles?: any;
  contactDetails?: ContactDetail[];
  sectionEnrollment?: any[];
  [key: string]: any;
}

/**
 * True for the "no email" placeholder that data entry leaves in contact rows
 * (local part "na", domain "na.com"). Compared part by part so the shipped
 * bundle holds no address-shaped string: scanners report any name-at-domain
 * text in served JavaScript (security audit: Email Address Disclosure).
 */
const isPlaceholderEmail = (email: string): boolean => {
  const [local, domain] = email.trim().toLowerCase().split('@');
  return local === 'na' && domain === 'na.com';
};

const SchoolFullDetailsPage: React.FC = () => {
  const { schoolID } = useParams<{ schoolID: string }>();
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState<SchoolFullDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFullSchoolData = async () => {
      try {
        setLoading(true);
        const data = await getSchoolFullDetails(schoolID!);
        // console.log('Full School Details Response:', data);
        // console.log('Type of data:', typeof data);
        // console.log('Data keys:', data ? Object.keys(data) : 'null');
        setSchoolData(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching school details:', err);
        setError(err.message || 'Failed to load school details');
      } finally {
        setLoading(false);
      }
    };

    if (schoolID) {
      fetchFullSchoolData();
    }
  }, [schoolID]);

  const carouselImages = [img1];

  // Get value with lookup mapping if applicable
  const renderValue = (value: any, lookupMap?: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    
    // Apply lookup mapping if provided
    if (lookupMap && value !== null && value !== undefined) {
      const mappedValue = lookupMap[String(value)];
      if (mappedValue) return mappedValue;
    }
    
    if (typeof value === 'boolean') return value ? 'YES' : 'NO';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Helper to get nested values from the API response structure
  const getValue = (key: string, lookupMap?: any): string => {
    if (!schoolData) return '-';
    
    // Check in schoolProfile first
    if (schoolData.schoolProfile && schoolData.schoolProfile[key] !== undefined) {
      return renderValue(schoolData.schoolProfile[key], lookupMap);
    }
    
    // Check in principalDetails
    if (schoolData.principalDetails && schoolData.principalDetails[key] !== undefined) {
      return renderValue(schoolData.principalDetails[key], lookupMap);
    }
    
    // Check in locationDetails nested objects
    if (schoolData.locationDetails) {
      if (schoolData.locationDetails.state && schoolData.locationDetails.state[key] !== undefined) {
        return renderValue(schoolData.locationDetails.state[key], lookupMap);
      }
      if (schoolData.locationDetails.district && schoolData.locationDetails.district[key] !== undefined) {
        return renderValue(schoolData.locationDetails.district[key], lookupMap);
      }
      if (schoolData.locationDetails.constituency && schoolData.locationDetails.constituency[key] !== undefined) {
        return renderValue(schoolData.locationDetails.constituency[key], lookupMap);
      }
    }
    
    // Check in administrativeDetails nested objects
    if (schoolData.administrativeDetails) {
      if (schoolData.administrativeDetails.ro && schoolData.administrativeDetails.ro[key] !== undefined) {
        return renderValue(schoolData.administrativeDetails.ro[key], lookupMap);
      }
      if (schoolData.administrativeDetails.station && schoolData.administrativeDetails.station[key] !== undefined) {
        return renderValue(schoolData.administrativeDetails.station[key], lookupMap);
      }
      if (schoolData.administrativeDetails[key] !== undefined) {
        return renderValue(schoolData.administrativeDetails[key], lookupMap);
      }
    }
    
    return '-';
  };

  const renderDataRow = (label: string, key: string, lookupMap?: any, fullWidth: boolean = false) => {
    const value = getValue(key, lookupMap);
    return fullWidth ? (
      <div className="col-span-1 md:col-span-3" style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
        <div className="font-semibold text-gray-700 py-2">{label}</div>
        <div className="text-gray-900 py-2">{value}</div>
      </div>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
        <div className="font-semibold text-gray-700 py-2">{label}</div>
        <div className="text-gray-900 py-2">{value}</div>
      </div>
    );
  };

  const renderSection = (title: string, icon: string, fields: Array<{ label: string; key: string; lookupMap?: any; fullWidth?: boolean }>) => {
    if (!schoolData) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>{icon}</span>
          <h3 className="font-bold text-sm">{title}</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            {fields.map(field => 
              renderDataRow(field.label, field.key, field.lookupMap, field.fullWidth)
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderInfrastructureTable = () => {
    const infrastructure = schoolData?.infrastructureDetails?.infrastructure;
    if (!infrastructure || !Array.isArray(infrastructure) || infrastructure.length === 0) return null;

    // Filter out items with 0 quantity for cleaner display
    const activeInfra = infrastructure.filter((item: any) => item.quantity > 0);
    if (activeInfra.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🏗️</span>
          <h3 className="font-bold text-sm">Infrastructure</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">S No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Infrastructure Item</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Quantity</th>
                {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Last Updated</th> */}
              </tr>
            </thead>
            <tbody>
              {activeInfra.map((item: any, index: number) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">{index + 1}</td>
                  <td className="px-4 py-2 text-sm">{item.infraName || '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.quantity ?? 0}</td>
                  {/* <td className="px-4 py-2 text-sm">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : '-'}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCWSNInfraTable = () => {
    const cwsnInfra = schoolData?.infrastructureDetails?.cwsnInfra;
    if (!cwsnInfra || !Array.isArray(cwsnInfra) || cwsnInfra.length === 0) return null;

    const item = cwsnInfra[0]; // Usually only one record
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>♿</span>
          <h3 className="font-bold text-sm">CWSN Infrastructure</h3>
          <span className="ml-auto text-xs">Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Floors in Building</div>
              <div className="text-gray-900 py-2">{item.floorInBuilding ?? '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Ramp in Ground Floor</div>
              <div className="text-gray-900 py-2">{item.rampInGroundFloor ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Ramp in All Floors</div>
              <div className="text-gray-900 py-2">{item.rampInAllFloor ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Toilet for Boys</div>
              <div className="text-gray-900 py-2">{item.toiletForBoys ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Toilet for Girls</div>
              <div className="text-gray-900 py-2">{item.toiletForGirl ? 'YES' : 'NO'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCWSNProfile = () => {
    const cwsnProfile = schoolData?.infrastructureDetails?.cwsnProfile;
    if (!cwsnProfile || !Array.isArray(cwsnProfile) || cwsnProfile.length === 0) return null;

    const profile = cwsnProfile[0];
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>👨‍🏫</span>
          <h3 className="font-bold text-sm">CWSN Profile - Teachers & Students</h3>
          <span className="ml-auto text-xs">Updated: {profile.updatedAt ? new Date(profile.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Teachers for Primary</div>
              <div className="text-gray-900 py-2">{profile.teacherForPrimary ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Teachers for Secondary</div>
              <div className="text-gray-900 py-2">{profile.teacherForSecondary ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Students in Primary</div>
              <div className="text-gray-900 py-2">{profile.studentInPrimary ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Students in Secondary</div>
              <div className="text-gray-900 py-2">{profile.studentInSecondary ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLibraryProfile = () => {
    const library = schoolData?.facilityProfiles?.libraryProfile;
    if (!library || !Array.isArray(library) || library.length === 0) return null;

    const lib = library[0];
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>📚</span>
          <h3 className="font-bold text-sm">Library Profile</h3>
          <span className="ml-auto text-xs">Updated: {lib.updatedAt ? new Date(lib.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Total Books (Hindi)</div>
              <div className="text-gray-900 py-2">{lib.totalBooksHindi ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Total Books (English)</div>
              <div className="text-gray-900 py-2">{lib.totalBooksEnglish ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Seating Capacity</div>
              <div className="text-gray-900 py-2">{lib.studentSeatingCapacity ?? '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Computers (Students)</div>
              <div className="text-gray-900 py-2">{lib.noComputerStudent ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Librarian Name</div>
              <div className="text-gray-900 py-2">{lib.librarianName || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Librarian Mobile</div>
              <div className="text-gray-900 py-2">{lib.librarianMobileNo || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Automation System</div>
              <div className="text-gray-900 py-2">{LOOKUP.LIBRARY_AUTOMATION_SOFTWARE_NAME[lib.libraryAutomationSystemName as keyof typeof LOOKUP.LIBRARY_AUTOMATION_SOFTWARE_NAME] || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Internet Available</div>
              <div className="text-gray-900 py-2">{lib.internateAvailableInLibrary ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">AC Units</div>
              <div className="text-gray-900 py-2">{lib.noAC ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCCTVProfile = () => {
    const cctv = schoolData?.facilityProfiles?.cctvProfile;
    if (!cctv || !Array.isArray(cctv) || cctv.length === 0) return null;

    const cctvData = cctv[0];
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>📹</span>
          <h3 className="font-bold text-sm">CCTV Profile</h3>
          <span className="ml-auto text-xs">Updated: {cctvData.updatedAt ? new Date(cctvData.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">CCTV Installed</div>
              <div className="text-gray-900 py-2">{cctvData.isCCTVInstalled ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Total Cameras</div>
              <div className="text-gray-900 py-2">{cctvData.totalCCTVCamera ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Functional Cameras</div>
              <div className="text-gray-900 py-2">{cctvData.totalFunctionalCCTV ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Recording Storage (Days)</div>
              <div className="text-gray-900 py-2">{cctvData.recordingStorage ?? '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">In Corridors</div>
              <div className="text-gray-900 py-2">{cctvData.isCameraInstalledInCorridors ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">In Rooms</div>
              <div className="text-gray-900 py-2">{cctvData.isCameraInstalledInRooms ? 'YES' : 'NO'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSectionEnrollment = () => {
    const sectionEnrollment = schoolData?.sectionEnrollment;
    if (!sectionEnrollment || !Array.isArray(sectionEnrollment) || sectionEnrollment.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>📋</span>
          <h3 className="font-bold text-sm">Section &amp; Enrollment</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Class</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Current Enrollment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Sanctioned Sections</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Expected Enrollment</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Proposed Sections</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Recommended by RO</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Remark by RO</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {sectionEnrollment.map((item: any, index: number) => (
                <tr key={item.classID ?? index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium">{item.className || item.class || '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.currentEnrollment ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.sanctionedSection ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.expectedEnrollment ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.proposedSection ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.recommendationByRO ?? '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.remarkByRO || '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-GB') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderClassSectionTable = () => {
    const studentStats = schoolData?.academicDetails?.studentStatistics;
    if (!studentStats || !Array.isArray(studentStats) || studentStats.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🎓</span>
          <h3 className="font-bold text-sm">Academic Details - Student Statistics (Class & Section wise)</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Class</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Section</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Stream</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Total Students</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Boys</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Girls</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">CWSN</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">GEN</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">OBC</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">SC</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">ST</th>
              </tr>
            </thead>
            <tbody>
              {studentStats.map((item: any, index: number) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">{item.class || '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.section || '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.stream === '0' ? '-' : item.stream}</td>
                  <td className="px-4 py-2 text-sm">{item.totalStudents ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalBoys ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalGirls ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalCWSN ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalGen ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalOBC ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalSC ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.totalST ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderICTProfile = () => {
    const ict = schoolData?.facilityProfiles?.ictProfile;
    if (!ict || !Array.isArray(ict) || ict.length === 0) return null;

    const ictData = ict[0];
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>💻</span>
          <h3 className="font-bold text-sm">ICT Profile</h3>
          <span className="ml-auto text-xs">Updated: {ictData.updatedAt ? new Date(ictData.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Computers for Students</div>
              <div className="text-gray-900 py-2">{ictData.totalComputersAvailableForStudents ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Computer Labs</div>
              <div className="text-gray-900 py-2">{ictData.noComputerLabs ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Labs with LAN</div>
              <div className="text-gray-900 py-2">{ictData.noComputerLabsLANConnectivity ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Labs with Internet</div>
              <div className="text-gray-900 py-2">{ictData.noComputerLabsWithInternatConnectivity ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Interactive Touch Panels (Classrooms)</div>
              <div className="text-gray-900 py-2">{ictData.interactiveTouchPanelinClassRoom ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Smart TVs</div>
              <div className="text-gray-900 py-2">{ictData.noOfSmartTV ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Internet Connectivity</div>
              <div className="text-gray-900 py-2">{LOOKUP.INTERNET_CONNECTIVITY[ictData.internetConnectivity as keyof typeof LOOKUP.INTERNET_CONNECTIVITY] || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Internet Type</div>
              <div className="text-gray-900 py-2">{LOOKUP.INTERNET_TYPE[ictData.internetType as keyof typeof LOOKUP.INTERNET_TYPE] || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Internet Plan</div>
              <div className="text-gray-900 py-2">{LOOKUP.INTERNET_PLAN[ictData.internetPlan as keyof typeof LOOKUP.INTERNET_PLAN] || '-'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSportProfileTable = () => {
    const sports = schoolData?.facilityProfiles?.sportProfile;
    if (!sports || !Array.isArray(sports) || sports.length === 0) return null;

    // Filter sports with quantity > 0 or with category/type filled
    const activeSports = sports.filter((sport: any) => 
      (sport.quantity && sport.quantity > 0) || 
      (sport.category && sport.category !== '')
    );
    if (activeSports.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>⚽</span>
          <h3 className="font-bold text-sm">Sport Facilities</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">S No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Sport Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Quality</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Size</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">Regional</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-b">National</th>
              </tr>
            </thead>
            <tbody>
              {activeSports.map((item: any, index: number) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">{index + 1}</td>
                  <td className="px-4 py-2 text-sm">{item.sportType ? (LOOKUP.SPORT_TITLE[item.sportType as keyof typeof LOOKUP.SPORT_TITLE] || item.sportType) : '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.category ? (LOOKUP.SPORT_CATEGORY[item.category as keyof typeof LOOKUP.SPORT_CATEGORY] || item.category) : '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.type ? (LOOKUP.SPORT_TYPE[item.type as keyof typeof LOOKUP.SPORT_TYPE] || item.type) : '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.quality ? (LOOKUP.SPORT_QUALITY[item.quality as keyof typeof LOOKUP.SPORT_QUALITY] || item.quality) : '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.quantity ?? 0}</td>
                  <td className="px-4 py-2 text-sm">{item.size ? `${item.size} ${LOOKUP.SPORT_MEASURE_UNIT[item.measurementUnit as keyof typeof LOOKUP.SPORT_MEASURE_UNIT] || ''}` : '-'}</td>
                  <td className="px-4 py-2 text-sm">{item.forRegional ? 'YES' : 'NO'}</td>
                  <td className="px-4 py-2 text-sm">{item.forNational ? 'YES' : 'NO'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderNCCProfile = () => {
    const nccProfile = schoolData?.facilityProfiles?.nccProfile;
    if (!nccProfile || !Array.isArray(nccProfile) || nccProfile.length === 0) return null;

    const ncc = nccProfile[0];
    if (!ncc.isNccAvailable) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🎖️</span>
          <h3 className="font-bold text-sm">NCC Profile</h3>
          <span className="ml-auto text-xs">Updated: {ncc.updatedAt ? new Date(ncc.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">NCC Available</div>
              <div className="text-gray-900 py-2">{ncc.isNccAvailable ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">NCC Group</div>
              <div className="text-gray-900 py-2">{LOOKUP.NCC_GROUP[ncc.nccGroup as keyof typeof LOOKUP.NCC_GROUP] || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Available Wing</div>
              <div className="text-gray-900 py-2">{LOOKUP.NCC_GROUP_WING[ncc.avaliableWing as keyof typeof LOOKUP.NCC_GROUP_WING] || '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Total Boy Cadets</div>
              <div className="text-gray-900 py-2">{ncc.totalBoyCadet ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Total Girl Cadets</div>
              <div className="text-gray-900 py-2">{ncc.totalGirlCadet ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">ANO CTO</div>
              <div className="text-gray-900 py-2">{ncc.anoCto ?? '-'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">ANO CTO Rank</div>
              <div className="text-gray-900 py-2">{LOOKUP.NCC_ANO_CTO_RANK[ncc.anoCtoRank as keyof typeof LOOKUP.NCC_ANO_CTO_RANK] || '-'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGreenBuilding = () => {
    const greenBuilding = schoolData?.facilityProfiles?.greenBuilding;
    if (!greenBuilding || !Array.isArray(greenBuilding) || greenBuilding.length === 0) return null;

    const green = greenBuilding[0];
    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🌱</span>
          <h3 className="font-bold text-sm">Green Building & Energy Initiatives</h3>
          <span className="ml-auto text-xs">Updated: {green.updatedAt ? new Date(green.updatedAt).toLocaleDateString('en-GB') : '-'}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Rain Water Harvesting System</div>
              <div className="text-gray-900 py-2">{green.isRainWaterSystem ? 'YES' : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Solar Power Plant</div>
              <div className="text-gray-900 py-2">{green.solarPowerPlant ? `${green.solarPowerPlant} KW` : 'NO'}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Energy Efficient Electric System</div>
              <div className="text-gray-900 py-2">{green.energyEfficientElectricSystem ?? 0}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '250px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Solar Lights Available</div>
              <div className="text-gray-900 py-2">{green.availSolarLight ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLabProfiles = () => {
    const labs = schoolData?.labProfiles;
    if (!labs) return null;

    const hasAnyLab = 
      (labs.atlLab && labs.atlLab.length > 0 && labs.atlLab[0].isAtlLabEstablish) ||
      (labs.arVrLab && labs.arVrLab.length > 0 && labs.arVrLab[0].isArVrLab) ||
      (labs.roboticsAiLab && labs.roboticsAiLab.length > 0 && labs.roboticsAiLab[0].isRoboticsAILab) ||
      (labs.digitalLanguageLab && labs.digitalLanguageLab.length > 0 && labs.digitalLanguageLab[0].isFunctional);

    if (!hasAnyLab) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🔬</span>
          <h3 className="font-bold text-sm">Advanced Lab Facilities</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {labs.atlLab && labs.atlLab.length > 0 && labs.atlLab[0].isAtlLabEstablish === 1 && (
              <div className="border border-gray-300 rounded-lg p-4">
                <h4 className="font-bold text-purple-700 mb-3">🏗️ ATL Lab</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">Funded By:</span><span>{LOOKUP.LAB_FUNDED_BY[labs.atlLab[0].fundedBy as keyof typeof LOOKUP.LAB_FUNDED_BY] || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Year Established:</span><span>{labs.atlLab[0].yearOfEstablish || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Received:</span><span>₹{labs.atlLab[0].amountReceived?.toLocaleString() || 0}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Spent:</span><span>₹{labs.atlLab[0].amountExpenditure?.toLocaleString() || 0}</span></div>
                </div>
              </div>
            )}
            {labs.arVrLab && labs.arVrLab.length > 0 && labs.arVrLab[0].isArVrLab === 1 && (
              <div className="border border-gray-300 rounded-lg p-4">
                <h4 className="font-bold text-purple-700 mb-3">🥽 AR/VR Lab</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">Funded By:</span><span>{LOOKUP.LAB_FUNDED_BY[labs.arVrLab[0].fundedBy as keyof typeof LOOKUP.LAB_FUNDED_BY] || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Year Established:</span><span>{labs.arVrLab[0].yearOfEstablish || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Received:</span><span>₹{labs.arVrLab[0].amountReceived?.toLocaleString() || 0}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Spent:</span><span>₹{labs.arVrLab[0].amountExpenditure?.toLocaleString() || 0}</span></div>
                </div>
              </div>
            )}
            {labs.roboticsAiLab && labs.roboticsAiLab.length > 0 && labs.roboticsAiLab[0].isRoboticsAILab === 1 && (
              <div className="border border-gray-300 rounded-lg p-4">
                <h4 className="font-bold text-purple-700 mb-3">🤖 Robotics & AI Lab</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">Funded By:</span><span>{LOOKUP.LAB_FUNDED_BY[labs.roboticsAiLab[0].fundedBy as keyof typeof LOOKUP.LAB_FUNDED_BY] || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Year Established:</span><span>{labs.roboticsAiLab[0].yearOfEstablish || '-'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Received:</span><span>₹{labs.roboticsAiLab[0].amountReceived?.toLocaleString() || 0}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Amount Spent:</span><span>₹{labs.roboticsAiLab[0].amountExpenditure?.toLocaleString() || 0}</span></div>
                </div>
              </div>
            )}
            {labs.digitalLanguageLab && labs.digitalLanguageLab.length > 0 && labs.digitalLanguageLab[0].isFunctional === 1 && (
              <div className="border border-gray-300 rounded-lg p-4">
                <h4 className="font-bold text-purple-700 mb-3">🗣️ Digital Language Lab</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="font-semibold">Computers:</span><span>{labs.digitalLanguageLab[0].noOfComputer || 0}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">AMC Given:</span><span>{labs.digitalLanguageLab[0].amcGivenOrNot === '1' ? 'YES' : 'NO'}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Software:</span><span>{labs.digitalLanguageLab[0].softwareInstalled || '-'}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContactDetails = () => {
    // Filter out invalid contacts
    const validContacts = schoolData?.contactDetails?.filter((contact) => {
      const isValidName = contact.name && contact.name.toUpperCase() !== 'NA';
      const isValidContact = contact.contactNo && contact.contactNo !== '0000000000';
      const isValidEmail = contact.emailID && !isPlaceholderEmail(contact.emailID);
      return isValidName && (isValidContact || isValidEmail);
    }) || [];

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>📞</span>
          <h3 className="font-bold text-sm">Contact Details</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          {/* School Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">School Contact No</div>
              <div className="text-gray-900 text-sm break-words">{getValue('contactNo')}</div>
            </div>
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">School Email ID</div>
              <div className="text-gray-900 text-sm break-all">{getValue('emailID')}</div>
            </div>
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">Website</div>
              <div className="text-gray-900 text-sm break-all">
                {getValue('website') !== '-' ? (
                  <a href={getValue('website')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {getValue('website')}
                  </a>
                ) : '-'}
              </div>
            </div>
          </div>

          {/* Contact Details from Array - Only Valid Contacts */}
          {validContacts.length > 0 && (
            <div className="mt-4 space-y-4">
              {validContacts.map((contact, index) => (
                <div key={contact.id} className="border-t pt-4">
                  <div className="bg-purple-50 px-4 py-2 rounded-md mb-3">
                    <h4 className="font-bold text-purple-700 text-sm">{contact.roleName}</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <div className="font-semibold text-gray-700 text-sm mb-1">Name</div>
                      <div className="text-gray-900 text-sm break-words">{contact.name || '-'}</div>
                    </div>
                    <div className="flex flex-col">
                      <div className="font-semibold text-gray-700 text-sm mb-1">Contact No</div>
                      <div className="text-gray-900 text-sm break-words">{contact.contactNo || '-'}</div>
                    </div>
                    <div className="flex flex-col">
                      <div className="font-semibold text-gray-700 text-sm mb-1">Email ID</div>
                      <div className="text-gray-900 text-sm break-all">{contact.emailID || '-'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Postal Address */}
          <div className="grid grid-cols-1 gap-4 mt-4 pt-4 border-t">
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">Postal Address 1</div>
              <div className="text-gray-900 text-sm break-words">{getValue('postalAddress1')}</div>
            </div>
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">Postal Address 2</div>
              <div className="text-gray-900 text-sm break-words">{getValue('postalAddress2')}</div>
            </div>
            <div className="flex flex-col">
              <div className="font-semibold text-gray-700 text-sm mb-1">Postal Address 3</div>
              <div className="text-gray-900 text-sm break-words">{getValue('postalAddress3')}</div>
            </div>
            <div className="flex flex-col md:w-1/3">
              <div className="font-semibold text-gray-700 text-sm mb-1">Pin Code</div>
              <div className="text-gray-900 text-sm break-words">{getValue('pinCode')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAdministrativeDetails = () => {
    if (!schoolData?.administrativeDetails) return null;

    return (
      <div className="mb-6">
        <div className="bg-[#6A1786] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
          <span>🏛️</span>
          <h3 className="font-bold text-sm">Administrative Details</h3>
          <span className="ml-auto text-xs">Updated: {new Date().toLocaleDateString('en-GB')}</span>
        </div>
        <div className="border border-gray-200 rounded-b-lg p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2">
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Regional Office</div>
              <div className="text-gray-900 py-2">{getValue('roName')}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Station Name</div>
              <div className="text-gray-900 py-2">{getValue('stationName')}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
              <div className="font-semibold text-gray-700 py-2">Station Type</div>
              <div className="text-gray-900 py-2">{getValue('stationType', LOOKUP.STATION_TYPE)}</div>
            </div>
            {schoolData.administrativeDetails.sponsoredByName && (
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
                <div className="font-semibold text-gray-700 py-2">Sponsored By</div>
                <div className="text-gray-900 py-2">{schoolData.administrativeDetails.sponsoredByName}</div>
              </div>
            )}
            {schoolData.administrativeDetails.constructionAgencyName && (
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr' }}>
                <div className="font-semibold text-gray-700 py-2">Construction Agency</div>
                <div className="text-gray-900 py-2">{schoolData.administrativeDetails.constructionAgencyName}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F5]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} height="h-[400px]" />
      <div className="w-full">
        <Breadcrumb />
      </div>
      
      <main className="flex-grow px-10 w-full flex flex-col py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#6A1786]">
            School Information
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-[#6A1786] text-white rounded-lg hover:bg-[#541365] transition-colors font-semibold"
          >
            ← Back
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <KVSLoader size={80} color="#6B1786" />
              <p className="text-gray-600 mt-6">Loading full school details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-xl font-semibold mb-4">⚠️ {error}</p>
              <p className="text-gray-600">Please try again later</p>
            </div>
          ) : schoolData ? (
            <div>
              {/* Basic Details Section */}
              {renderSection('School Information', '📚', [
                { label: 'School Name', key: 'schoolName' },
                { label: 'Region Name', key: 'roName' },
                { label: 'Station Name', key: 'stationName' },
                { label: 'Station Type', key: 'stationType', lookupMap: LOOKUP.STATION_TYPE },
                { label: 'Location Type', key: 'locationType', lookupMap: LOOKUP.LOCATION_TYPE },
                { label: 'Sponsored By', key: 'sponsoredBy', lookupMap: LOOKUP.SPONSORED_BY },
                { label: 'Sponsoring Agency', key: 'sponsoringAgency' },
                { label: 'School Building', key: 'schoolBuilding', lookupMap: LOOKUP.SCHOOL_BUILDING },
                { label: 'Building Type', key: 'buildingType', lookupMap: LOOKUP.INFRA_SIZE_TYPE },
                { label: 'Infra Facility', key: 'infraFacility', lookupMap: LOOKUP.INFRA_FACILITY },
                { label: 'PM SHRI', key: 'isPMShree', lookupMap: LOOKUP.PM_SHRI_PHASE },
                { label: 'Opening Year', key: 'openingYear' },
                { label: 'Establishment Year', key: 'establishmentYear' },
                { label: 'UDISE Code', key: 'udiseCode' },
                { label: 'CBSE School Code', key: 'cbseSchoolCode' },
                { label: 'CBSE Affiliation', key: 'cbseAffliation' },
                { label: 'Land Ownership Type', key: 'ownershipType', lookupMap: LOOKUP.OWNERSHIP_TYPE },
                { label: 'Year of Permanent Building', key: 'yearOfPermanentBuilding' },
                { label: 'Construction Agency', key: 'constructionAgency' },
              ])}

              {/* Location Section */}
              {renderSection('Location', '📍', [
                { label: 'State', key: 'stateName' },
                { label: 'District', key: 'districtName' },
                { label: 'Constituency', key: 'constituencyName' },
                { label: 'City', key: 'city' },
                { label: 'Longitude', key: 'longitude' },
                { label: 'Latitude', key: 'latitude' },
                { label: 'Area of Land (in Acre)', key: 'areaOfLand' },
              ])}

              {/* Contact Details Section */}
              {renderContactDetails()}

              {/* Administrative Details Section */}
              {renderAdministrativeDetails()}

              {/* Infrastructure Table */}
              {renderInfrastructureTable()}

              {/* CWSN Infrastructure */}
              {renderCWSNInfraTable()}

              {/* CWSN Profile */}
              {renderCWSNProfile()}

              {/* ICT Profile */}
              {renderICTProfile()}

              {/* Library Profile */}
              {renderLibraryProfile()}

              {/* CCTV Profile */}
              {renderCCTVProfile()}

              {/* Sport Facilities */}
              {renderSportProfileTable()}

              {/* NCC Profile */}
              {renderNCCProfile()}

              {/* Green Building */}
              {renderGreenBuilding()}

              {/* Advanced Lab Facilities */}
              {renderLabProfiles()}

              {/* Section & Enrollment */}
              {renderSectionEnrollment()}

              {/* Class & Section Details */}
              {renderClassSectionTable()}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-600">No data available</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SchoolFullDetailsPage;
